import { useEffect, useMemo, useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import {
  getImageUrl,
  locationApi,
  serviceApi,
  specializationApi,
  userMasterApi,
  userRoleApi,
} from "../../api/masterApi";
import DropdownMultiSelect from "../../components/DropdownMultiSelect";
import { getAdminUser } from "../../utils/auth";
import { notifyProfileUpdated } from "../../utils/profileCompletion";
import "./UserMaster.css";

const boolFields = new Set([
  "is_establishment",
  "education_verified",
  "criminal_record_verified",
  "business_integrity_verified",
  "kyc_verified",
  "bank_details_verified",
]);
const arrayFields = [
  "linked_asset_ids",
  "specialization_ids",
  "phone_numbers",
  "email_addresses",
  "picture_gallery",
  "video_gallery",
  "educational_qualifications",
];
const singleFileFields = [
  "profile_picture",
  "profile_pdf",
  "education_verification_certificate",
  "criminal_record_verification_certificate",
  "business_integrity_verification_certificate",
  "registration_certificate",
  "pan_image",
  "aadhar_front",
  "aadhar_back",
  "gst_certificate",
  "kyc_verification_certificate",
  "bank_details_verification_certificate",
];
const dateFields = [
  "education_verification_date",
  "criminal_record_verification_date",
  "business_integrity_verification_date",
  "kyc_verification_date",
  "bank_details_verification_date",
];

/** Fields filled by admin — shown prefilled and locked for providers */
const ADMIN_LOCKED_FIELDS = new Set([
  "user_role_id",
  "is_establishment",
  "user_title",
  "first_name",
  "last_name",
  "gender",
  "age",
  "experience_years",
  "status",
  "location_id",
  "phone_numbers",
  "country_code",
  "linked_asset_ids",
  "education_verified",
  "education_verification_by",
  "education_verification_date",
  "education_verification_certificate",
  "criminal_record_verified",
  "criminal_record_verification_by",
  "criminal_record_verification_date",
  "criminal_record_verification_certificate",
  "business_integrity_verified",
  "business_integrity_verification_by",
  "business_integrity_verification_date",
  "business_integrity_verification_certificate",
  "kyc_verified",
  "kyc_verification_by",
  "kyc_verification_date",
  "kyc_verification_certificate",
  "bank_details_verified",
  "bank_details_verification_by",
  "bank_details_verification_date",
  "bank_details_verification_certificate",
]);

const emptyForm = () => ({
  user_role_id: "",
  is_establishment: false,
  user_title: "",
  first_name: "",
  last_name: "",
  gender: "",
  age: "",
  experience_years: "",
  linked_asset_ids: [],
  specialization_ids: [],
  profile_picture: "",
  video_bio_url: "",
  brief_bio: "",
  profile_pdf: "",
  picture_gallery: [],
  video_gallery: [],
  location_id: "",
  country_code: "",
  phone_numbers: [""],
  email_addresses: [""],
  educational_qualifications: [],
  education_verified: false,
  education_verification_by: "",
  education_verification_date: "",
  education_verification_certificate: "",
  criminal_record_verified: false,
  criminal_record_verification_by: "",
  criminal_record_verification_date: "",
  criminal_record_verification_certificate: "",
  business_integrity_verified: false,
  business_integrity_verification_by: "",
  business_integrity_verification_date: "",
  business_integrity_verification_certificate: "",
  incorporation_year: "",
  registration_number: "",
  registration_certificate: "",
  registration_body: "",
  pan_number: "",
  pan_image: "",
  aadhar_number: "",
  aadhar_front: "",
  aadhar_back: "",
  gst_number: "",
  gst_certificate: "",
  kyc_verified: false,
  kyc_verification_by: "",
  kyc_verification_date: "",
  kyc_verification_certificate: "",
  bank_name: "",
  bank_branch_address: "",
  ifsc_code: "",
  bank_account_number: "",
  bank_details_verified: false,
  bank_details_verification_by: "",
  bank_details_verification_date: "",
  bank_details_verification_certificate: "",
  status: "Active",
});

function basename(value = "") {
  return String(value).split("/").pop();
}

function VerifiedBadge({ label, by, date, certificate }) {
  const details = [
    by ? `By ${by}` : null,
    date ? `on ${date}` : null,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="user-master-verify-badge">
      <span className="user-master-verify-badge__mark" aria-hidden="true">
        ✓
      </span>
      <div className="user-master-verify-badge__body">
        <strong>{label}</strong>
        {details && <span>{details}</span>}
        {certificate && (
          <a href={getImageUrl(certificate)} target="_blank" rel="noreferrer">
            View certificate
          </a>
        )}
      </div>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <section className="user-master-section">
      <h3>
        {number}. {title}
      </h3>
      <div className="user-master-grid">{children}</div>
    </section>
  );
}

function VerificationSection({ number, title, badges = [], children }) {
  const visibleBadges = badges.filter((badge) => badge?.verified);
  return (
    <section className="user-master-section user-master-section--verification">
      <h3>
        {number}. {title}
        <span className="user-master-section__tag"> Readonly</span>
      </h3>
      {visibleBadges.length > 0 && (
        <div className="user-master-verify-list">
          {visibleBadges.map((badge) => (
            <VerifiedBadge
              key={badge.label}
              label={badge.label}
              by={badge.by}
              date={badge.date}
              certificate={badge.certificate}
            />
          ))}
        </div>
      )}
      <div className="user-master-grid">{children}</div>
    </section>
  );
}

function FileField({ label, name, accept, multiple, existing, onChange, disabled }) {
  const existingFiles = Array.isArray(existing) ? existing : [existing].filter(Boolean);
  return (
    <div className="user-master-field">
      <label htmlFor={name}>{label}</label>
      <input
        id={name}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(event) => onChange(name, Array.from(event.target.files || []))}
      />
      {existingFiles.length > 0 && (
        <div className="user-master-existing">
          {existingFiles.map((file, index) => (
            <a key={`${name}-${index}`} href={getImageUrl(file)} target="_blank" rel="noreferrer">
              {basename(file)}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function ArrayInput({ label, values, type = "text", placeholder, onChange, disabled }) {
  const list = values?.length ? values : [""];
  return (
    <div className="user-master-field user-master-field--full">
      <label>{label}</label>
      <div className="user-master-repeat-list">
        {list.map((value, index) => (
          <div className="user-master-repeat-row" key={`${label}-${index}`}>
            <input
              type={type}
              value={value}
              placeholder={placeholder}
              readOnly={disabled}
              onChange={(event) => {
                if (disabled) return;
                const next = [...list];
                next[index] = event.target.value;
                onChange(next);
              }}
            />
            {!disabled && list.length > 1 && (
              <button type="button" onClick={() => onChange(list.filter((_, i) => i !== index))}>
                Remove
              </button>
            )}
          </div>
        ))}
        {!disabled && (
          <button type="button" className="user-master-add" onClick={() => onChange([...list, ""])}>
            + Add more
          </button>
        )}
      </div>
    </div>
  );
}

function RecordRepeater({ label, rows, kind, onChange }) {
  const add = () => {
    const row =
      kind === "education"
        ? { name: "", year: "", image: "", file: null }
        : { title: "", image: "", file: null };
    onChange([...(rows || []), row]);
  };
  return (
    <div className="user-master-field user-master-field--full">
      <label>{label}</label>
      <div className="user-master-records">
        {(rows || []).map((row, index) => (
          <div className="user-master-record" key={`${kind}-${index}`}>
            <input
              type="text"
              value={kind === "education" ? row.name || "" : row.title || ""}
              placeholder={kind === "education" ? "Qualification name" : "Title"}
              onChange={(event) => {
                const next = [...rows];
                next[index] = {
                  ...row,
                  [kind === "education" ? "name" : "title"]: event.target.value,
                };
                onChange(next);
              }}
            />
            {kind === "education" && (
              <input
                type="number"
                value={row.year || ""}
                placeholder="Year"
                onChange={(event) => {
                  const next = [...rows];
                  next[index] = { ...row, year: event.target.value };
                  onChange(next);
                }}
              />
            )}
            <input
              type="file"
              accept="image/*"
              onChange={(event) => {
                const next = [...rows];
                next[index] = { ...row, file: event.target.files?.[0] || null };
                onChange(next);
              }}
            />
            {row.image && (
              <a href={getImageUrl(row.image)} target="_blank" rel="noreferrer">
                Current image
              </a>
            )}
            <button type="button" onClick={() => onChange(rows.filter((_, i) => i !== index))}>
              Remove
            </button>
          </div>
        ))}
        <button type="button" className="user-master-add" onClick={add}>
          + Add item
        </button>
      </div>
    </div>
  );
}

function mapUserToForm(item) {
  const next = emptyForm();
  Object.keys(next).forEach((key) => {
    if (item[key] !== null && item[key] !== undefined) next[key] = item[key];
  });
  boolFields.forEach((key) => {
    next[key] = Number(item[key]) === 1;
  });
  arrayFields.forEach((key) => {
    next[key] = Array.isArray(item[key]) ? item[key] : [];
  });
  dateFields.forEach((key) => {
    next[key] = item[key] ? String(item[key]).slice(0, 10) : "";
  });
  next.user_role_id = String(item.user_role_id || "");
  next.location_id = String(item.location_id || "");
  next.location_name = item.location_name || "";
  next.linked_asset_ids = next.linked_asset_ids.map(String);
  next.specialization_ids = next.specialization_ids.map(String);
  if (!next.phone_numbers.length) next.phone_numbers = [""];
  if (!next.email_addresses.length) next.email_addresses = [""];
  return next;
}

export default function UserMasterProfile() {
  const authUser = getAdminUser();
  const userId = authUser?.reference_id;

  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState({});
  const [options, setOptions] = useState({
    roles: [],
    assets: [],
    specializations: [],
    locations: [],
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const showToast = (message, severity = "success") =>
    setToast({ open: true, message, severity });

  const loadProfile = async () => {
    if (!userId) {
      showToast("No user profile is linked to this login", "error");
      return;
    }
    setLoading(true);
    try {
      const [user, roles, assets, specializations, locations] = await Promise.all([
        userMasterApi.getOne(userId),
        userRoleApi.list(),
        serviceApi.list(),
        specializationApi.list(),
        locationApi.list(),
      ]);
      setOptions({ roles, assets, specializations, locations });
      setForm(mapUserToForm(user));
      setFiles({});
      notifyProfileUpdated({ profile: user });
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (loading || !userId) return;
    notifyProfileUpdated({
      profile: {
        ...form,
        profile_pdf:
          files.profile_pdf?.[0] != null ? form.profile_pdf || "selected" : form.profile_pdf,
        picture_gallery:
          files.picture_gallery?.length
            ? [...(form.picture_gallery || []), "selected"]
            : form.picture_gallery,
        profile_picture:
          files.profile_picture?.[0] != null
            ? form.profile_picture || "selected"
            : form.profile_picture,
      },
    });
  }, [form, files, loading, userId]);

  const locationOptions = useMemo(
    () =>
      [...options.locations].sort((a, b) =>
        String(a.location_name || "").localeCompare(String(b.location_name || ""))
      ),
    [options.locations]
  );

  const setValue = (name, value) => {
    if (ADMIN_LOCKED_FIELDS.has(name)) return;
    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const isLocked = (name) => ADMIN_LOCKED_FIELDS.has(name);

  const verificationBadge = (prefix, label) => ({
    verified: Boolean(form[`${prefix}_verified`]),
    label,
    by: form[`${prefix}_verification_by`] || "",
    date: form[`${prefix}_verification_date`] || "",
    certificate: form[`${prefix}_verification_certificate`] || "",
  });

  const renderVerification = (prefix, labels) => (
    <>
      {renderField({
        name: `${prefix}_verified`,
        label: labels.verified,
        type: "boolean",
        readOnly: true,
      })}
      {renderField({
        name: `${prefix}_verification_by`,
        label: labels.by,
        readOnly: true,
      })}
      {renderField({
        name: `${prefix}_verification_date`,
        label: labels.date,
        type: "date",
        readOnly: true,
      })}
      <FileField
        label={labels.certificate}
        name={`${prefix}_verification_certificate`}
        accept="image/*,.pdf,application/pdf"
        existing={form[`${prefix}_verification_certificate`]}
        disabled
        onChange={() => {}}
      />
    </>
  );

  const renderField = ({
    name,
    label,
    type = "text",
    options: fieldOptions = [],
    required,
    readOnly,
    full,
    placeholder,
  }) => {
    const locked = readOnly || isLocked(name);
    return (
      <div className={`user-master-field ${full ? "user-master-field--full" : ""}`} key={name}>
        <label htmlFor={name}>
          {label}
          {required && <span className="user-master-req"> *</span>}
        </label>
        {type === "select" ? (
          <select
            id={name}
            value={form[name]}
            disabled={locked}
            onChange={(e) => setValue(name, e.target.value)}
          >
            <option value="">Select {label.toLowerCase()}</option>
            {fieldOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : type === "multiselect" ? (
          <DropdownMultiSelect
            id={name}
            options={fieldOptions}
            value={form[name] || []}
            disabled={locked}
            placeholder={`Select ${label.toLowerCase()}`}
            onChange={(next) => setValue(name, next)}
          />
        ) : type === "textarea" ? (
          <textarea
            id={name}
            rows={4}
            value={form[name] || ""}
            placeholder={placeholder}
            readOnly={locked}
            onChange={(e) => setValue(name, e.target.value)}
          />
        ) : type === "boolean" ? (
          <select
            id={name}
            value={form[name] ? "1" : "0"}
            disabled={locked}
            onChange={(e) => setValue(name, e.target.value === "1")}
          >
            <option value="0">No</option>
            <option value="1">Yes</option>
          </select>
        ) : (
          <input
            id={name}
            type={type}
            value={form[name] || ""}
            placeholder={placeholder}
            readOnly={locked}
            onChange={(e) => setValue(name, e.target.value)}
          />
        )}
      </div>
    );
  };

  const appendRepeater = (fd, field, uploadField, items) => {
    let uploadIndex = 0;
    const metadata = (items || []).map(({ file, ...item }) => {
      if (!file) return { ...item, image: item.image ? basename(item.image) : null };
      fd.append(uploadField, file);
      const next = { ...item, image: null, upload_index: uploadIndex };
      uploadIndex += 1;
      return next;
    });
    fd.append(field, JSON.stringify(metadata));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!userId) {
      showToast("No user profile is linked to this login", "error");
      return;
    }
    if (!form.user_role_id || !form.first_name.trim()) {
      showToast("User Role and First Name are required", "error");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([name, value]) => {
        if (arrayFields.includes(name) || singleFileFields.includes(name)) return;
        fd.append(name, boolFields.has(name) ? (value ? "1" : "0") : value ?? "");
      });
      [
        "specialization_ids",
        "phone_numbers",
        "email_addresses",
      ].forEach((name) => fd.append(name, JSON.stringify((form[name] || []).filter(Boolean))));
      singleFileFields.forEach((name) => {
        if (files[name]?.[0]) fd.append(name, files[name][0]);
        else if (form[name]) fd.append(name, basename(form[name]));
      });
      ["picture_gallery", "video_gallery"].forEach((name) => {
        fd.append(name, JSON.stringify((form[name] || []).map(basename)));
        (files[name] || []).forEach((file) => fd.append(name, file));
      });
      appendRepeater(
        fd,
        "educational_qualifications",
        "education_images",
        form.educational_qualifications
      );

      await userMasterApi.update(userId, fd);
      showToast("Profile updated successfully");
      await loadProfile();
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const roleOptions = options.roles.map((role) => ({
    value: role.user_role_id,
    label: role.user_role,
  }));
  const assetOptions = options.assets.map((asset) => ({
    value: asset.service_id,
    label: asset.service_title || "Asset",
  }));
  const specializationOptions = options.specializations.map((item) => ({
    value: item.specialization_id,
    label: item.specialization_name,
  }));

  return (
    <div className="user-master-page">
      <div className="user-master-header">
        <h2>User Master</h2>
        <p>
          Complete your profile. Basic information, location and phone filled by admin are
          prefilled; update the remaining sections below.
        </p>
      </div>

      {loading ? (
        <section className="user-master-section">
          <p className="user-master-hint">Loading your profile…</p>
        </section>
      ) : (
        <form onSubmit={handleSubmit}>
          <Section number="1" title="Basic Information">
            {renderField({
              name: "user_role_id",
              label: "User Role",
              type: "select",
              options: roleOptions,
              required: true,
            })}
            {renderField({
              name: "is_establishment",
              label: "Is Establishment",
              type: "boolean",
            })}
            {renderField({
              name: "user_title",
              label: "User Title",
              type: "select",
              options: ["Dr", "Mr", "Ms"].map((v) => ({ value: v, label: v })),
            })}
            {renderField({ name: "first_name", label: "First Name", required: true })}
            {renderField({ name: "last_name", label: "Last Name" })}
            {renderField({
              name: "gender",
              label: "Gender",
              type: "select",
              options: ["Male", "Female", "Other"].map((v) => ({ value: v, label: v })),
            })}
            {renderField({ name: "age", label: "Age", type: "number" })}
            {renderField({
              name: "experience_years",
              label: "Experience in Years",
              type: "number",
            })}
            <FileField
              label="Profile Picture"
              name="profile_picture"
              accept="image/*"
              existing={form.profile_picture}
              onChange={(name, selected) => setFiles((prev) => ({ ...prev, [name]: selected }))}
            />
            {renderField({
              name: "status",
              label: "Status",
              type: "select",
              options: ["Active", "Suspended", "Blocked"].map((v) => ({ value: v, label: v })),
            })}
          </Section>

          <Section number="2" title="Professional Profile">
            {renderField({
              name: "linked_asset_ids",
              label: "Linked Asset",
              type: "multiselect",
              options: assetOptions,
              full: true,
            })}
            {renderField({
              name: "specialization_ids",
              label: "Specialization",
              type: "multiselect",
              options: specializationOptions,
              full: true,
            })}
            {renderField({
              name: "video_bio_url",
              label: "Video Bio (YouTube Link)",
              type: "url",
            })}
            <FileField
              label="Profile PDF"
              name="profile_pdf"
              accept=".pdf,application/pdf"
              existing={form.profile_pdf}
              onChange={(name, selected) => setFiles((prev) => ({ ...prev, [name]: selected }))}
            />
            {renderField({
              name: "brief_bio",
              label: "Brief Bio",
              type: "textarea",
              full: true,
            })}
          </Section>

          <Section number="3" title="Media Gallery">
            <FileField
              label="Picture Gallery"
              name="picture_gallery"
              accept="image/*"
              multiple
              existing={form.picture_gallery}
              onChange={(name, selected) => setFiles((prev) => ({ ...prev, [name]: selected }))}
            />
            <FileField
              label="Video Gallery"
              name="video_gallery"
              accept="video/*"
              multiple
              existing={form.video_gallery}
              onChange={(name, selected) => setFiles((prev) => ({ ...prev, [name]: selected }))}
            />
          </Section>

          <Section number="4" title="Contact Information">
            <div className="user-master-field">
              <label htmlFor="location_id">Location</label>
              <select id="location_id" value={form.location_id} disabled>
                <option value="">Select location</option>
                {locationOptions.map((location) => (
                  <option key={location.location_id} value={location.location_id}>
                    {location.location_name}
                  </option>
                ))}
                {form.location_id &&
                  !locationOptions.some(
                    (location) => String(location.location_id) === String(form.location_id)
                  ) && (
                    <option value={form.location_id}>
                      {form.location_name || "Location"}
                    </option>
                  )}
              </select>
            </div>
            {renderField({ name: "country_code", label: "Country Code", readOnly: true })}
            <ArrayInput
              label="Phone Number"
              values={form.phone_numbers}
              placeholder="Phone number"
              disabled
              onChange={(value) => setValue("phone_numbers", value)}
            />
            <ArrayInput
              label="Email Address"
              type="email"
              values={form.email_addresses}
              placeholder="email@example.com"
              onChange={(value) => setValue("email_addresses", value)}
            />
          </Section>

          <Section number="5" title="Education and Background">
            <RecordRepeater
              label="Educational Qualification"
              kind="education"
              rows={form.educational_qualifications}
              onChange={(value) => setValue("educational_qualifications", value)}
            />
          </Section>

          <VerificationSection
            number="5.1"
            title="Education Verification"
            badges={[verificationBadge("education", "Educational Qualification Verified")]}
          >
            {renderVerification("education", {
              verified: "Is Educational Qualification Verified",
              by: "Education Qualification Verification By",
              date: "Educational Qualification Verification Date",
              certificate: "Education Verification Certificate",
            })}
          </VerificationSection>

          <VerificationSection
            number="5.2"
            title="Criminal Record Verification"
            badges={[verificationBadge("criminal_record", "Criminal Record Verified")]}
          >
            {renderVerification("criminal_record", {
              verified: "Criminal Record Verified",
              by: "Criminal Record Verification By",
              date: "Criminal Record Verification Date",
              certificate: "Criminal Record Verification Certificate",
            })}
          </VerificationSection>

          <VerificationSection
            number="5.3"
            title="Business Integrity Verification"
            badges={[verificationBadge("business_integrity", "Business Integrity Verified")]}
          >
            {renderVerification("business_integrity", {
              verified: "Business Integrity Verified",
              by: "Business Integrity Verification By",
              date: "Business Integrity Verification Date",
              certificate: "Business Integrity Verification Certificate",
            })}
          </VerificationSection>

          <Section number="6" title="Business Registration and KYC">
            {renderField({
              name: "incorporation_year",
              label: "Incorporation Year",
              type: "number",
            })}
            {renderField({ name: "registration_number", label: "Registration Number" })}
            {renderField({ name: "registration_body", label: "Registration Body" })}
            <FileField
              label="Registration Certificate"
              name="registration_certificate"
              accept="image/*,.pdf"
              existing={form.registration_certificate}
              onChange={(name, selected) => setFiles((prev) => ({ ...prev, [name]: selected }))}
            />
            {renderField({ name: "pan_number", label: "PAN Card No" })}
            <FileField
              label="PAN Card Image"
              name="pan_image"
              accept="image/*"
              existing={form.pan_image}
              onChange={(name, selected) => setFiles((prev) => ({ ...prev, [name]: selected }))}
            />
            {renderField({ name: "aadhar_number", label: "AADHAR Card No" })}
            <FileField
              label="AADHAR Card Front"
              name="aadhar_front"
              accept="image/*"
              existing={form.aadhar_front}
              onChange={(name, selected) => setFiles((prev) => ({ ...prev, [name]: selected }))}
            />
            <FileField
              label="AADHAR Card Back"
              name="aadhar_back"
              accept="image/*"
              existing={form.aadhar_back}
              onChange={(name, selected) => setFiles((prev) => ({ ...prev, [name]: selected }))}
            />
            {renderField({ name: "gst_number", label: "GST No" })}
            <FileField
              label="GST Certificate"
              name="gst_certificate"
              accept="image/*,.pdf"
              existing={form.gst_certificate}
              onChange={(name, selected) => setFiles((prev) => ({ ...prev, [name]: selected }))}
            />
          </Section>

          <VerificationSection
            number="6.1"
            title="Business / User KYC Verification"
            badges={[verificationBadge("kyc", "Business / User KYC Verified")]}
          >
            {renderVerification("kyc", {
              verified: "Business / User KYC Verified",
              by: "Business / User KYC Verification By",
              date: "Business / User KYC Verification Date",
              certificate: "Business / User KYC Verification Certificate",
            })}
          </VerificationSection>

          <Section number="7" title="Bank Details">
            {renderField({ name: "bank_name", label: "Bank Name" })}
            {renderField({ name: "bank_branch_address", label: "Bank Branch Address" })}
            {renderField({ name: "ifsc_code", label: "IFSC Code" })}
            {renderField({ name: "bank_account_number", label: "Bank Account Number" })}
          </Section>

          <VerificationSection
            number="7.1"
            title="Bank Details Verification"
            badges={[verificationBadge("bank_details", "Bank Details Verified")]}
          >
            {renderVerification("bank_details", {
              verified: "Bank Details Verified",
              by: "Bank Details Verification By",
              date: "Bank Details Verification Date",
              certificate: "Bank Details Verification Certificate",
            })}
          </VerificationSection>

          <div className="user-master-actions">
            <button type="submit" className="user-master-save" disabled={saving || !userId}>
              {saving ? "Saving..." : "Update Profile"}
            </button>
          </div>
        </form>
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}